import { useEffect, useRef, useState, useCallback } from 'react';
import { View, Pressable, Platform, Animated, StyleSheet } from 'react-native';
import { Text } from 'tamagui';
import * as WebBrowser from 'expo-web-browser';
import { HIDE_LOADING_MODAL, SHOW_AUTH_MODAL, SHOW_LOADING_MODAL } from '@reduxLocal/actions/types';
import { DefaultRootState } from '@reduxLocal/persistState';
import { createURL } from 'expo-linking';
import WeChatIcon from '@assets/--native/Icons/wechat-icon';
import apis from '@api/api';
import { useDispatch, useSelector } from 'react-redux';
import React from 'react';
import styles from '@stylesheet';
import { responsiveFontSize } from '@utils/responsiveFontSize';
import { useNavigation } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import {
  handleError,
  handleOAuthResponse,
} from './utils/authUtils';
import { getAuthSessionGuard } from '@utils/authSessionGuard';
import { useTranslation } from 'react-i18next';

try { WebBrowser.maybeCompleteAuthSession({ skipRedirectCheck: true }); } catch (e) { console.warn('maybeCompleteAuthSession error:', e); }

const authGuard = getAuthSessionGuard();
const WECHAT_LOGIN_STATE_PREFIX = 'wechat_login';

function isWeChatBrowser(): boolean {
  if (Platform.OS !== 'web' || typeof navigator === 'undefined') return false;
  return /micromessenger/i.test(navigator.userAgent);
}

function buildRedirectUri(): string {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    return `${window.location.origin}${window.location.pathname}`;
  }
  return createURL('');
}

function parseWeChatCallbackFromUrl(): { code: string; state: string } | null {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  const state = params.get('state') || '';
  if (!code || !state.startsWith(WECHAT_LOGIN_STATE_PREFIX)) return null;
  return { code, state };
}

function clearWeChatCallbackParams(): void {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  url.searchParams.delete('code');
  url.searchParams.delete('state');
  window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
}

const WeChatButton = ({ code, productId }: {
  code?: string;
  productId?: string;
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const handledCallbackRef = useRef(false);
  const navigation = useNavigation();
  const notificationToken: string = useSelector(
    (state: DefaultRootState) => state.app.notificationToken
  );
  const theme = useSelector((state: DefaultRootState) => state.app.theme.lightTheme);
  const buttonColors = {
    background: theme ? '#ffffff' : 'rgba(30, 41, 59, 0.95)',
    text: theme ? '#111827' : '#e2e8f0',
    border: theme ? '#e5e7eb' : 'rgba(148, 163, 184, 0.35)',
    hover: theme ? 'rgba(7, 193, 96, 0.15)' : 'rgba(7, 193, 96, 0.2)',
    shadow: theme ? '#000000' : '#020617',
  };

  useEffect(() => {
    Animated.timing(fadeAnim, {
      toValue: 1,
      duration: 1000,
      useNativeDriver: true,
    }).start();
  }, [fadeAnim]);

  const completeWeChatOAuth = useCallback(async (authCode: string, redirectUri: string) => {
    if (!authCode || handledCallbackRef.current) return;
    handledCallbackRef.current = true;

    try {
      dispatch({ type: SHOW_LOADING_MODAL });
      const res = await apis.wechatOAuthLogin({
        code: authCode,
        redirectUri,
        promoCode: code,
        productId,
        notificationToken,
      });
      await handleOAuthResponse(res, dispatch, navigation, notificationToken, 'WeChat');
    } catch (e) {
      handleError(dispatch, 'WeChat login failed, please try again. If the issue persists, contact support.');
    } finally {
      dispatch({ type: HIDE_LOADING_MODAL });
      clearWeChatCallbackParams();
    }
  }, [code, productId, dispatch, navigation, notificationToken]);

  // Handle redirect return from WeChat OAuth on web.
  useEffect(() => {
    const callback = parseWeChatCallbackFromUrl();
    if (!callback) return;
    void completeWeChatOAuth(callback.code, buildRedirectUri());
  }, [completeWeChatOAuth]);

  const handleClick = async () => {
    try {
      if (authGuard.isSessionActive()) {
        const activeSession = authGuard.getActiveSession();
        dispatch({
          type: SHOW_AUTH_MODAL,
          data: {
            content: `Please wait for ${activeSession} authentication to complete`,
            type: 'error',
          },
        });
        return;
      }

      handledCallbackRef.current = false;
      const redirectUri = buildRedirectUri();
      const state = `${WECHAT_LOGIN_STATE_PREFIX}_${Date.now()}`;

      const { authUrl } = await apis.getWeChatAuthUrl({
        redirectUri,
        state,
        inWeChatBrowser: isWeChatBrowser(),
      });

      if (!authUrl) {
        handleError(dispatch, 'WeChat login is not configured. Please contact support.');
        return;
      }

      const res = await authGuard.startSession('WeChat', async () => {
        if (Platform.OS === 'web') {
          window.location.assign(authUrl);
          return { type: 'dismiss' as const };
        }
        return WebBrowser.openAuthSessionAsync(authUrl, redirectUri);
      });

      if (Platform.OS !== 'web' && res?.type === 'success' && res.url) {
        try {
          const callbackUrl = new URL(res.url);
          const authCode = callbackUrl.searchParams.get('code');
          const callbackState = callbackUrl.searchParams.get('state') || '';
          if (authCode && callbackState.startsWith(WECHAT_LOGIN_STATE_PREFIX)) {
            await completeWeChatOAuth(authCode, redirectUri);
          }
        } catch (parseError) {
          console.error('WeChat callback parse error:', parseError);
          handleError(dispatch, 'WeChat login failed, please try again.');
        }
      }
    } catch (error: any) {
      console.error('WeChat auth error:', error);
      handleError(dispatch, 'An unexpected error occurred during WeChat login. Please try again.');
    }
  };

  return (
    <Pressable
      onPress={handleClick}
      style={{
        minHeight: Platform.select({ web: 50, default: 50 }),
        height: 50,
        maxHeight: 50,
        width: '100%',
        maxWidth: Platform.OS === 'web' ? 400 : undefined,
        alignSelf: Platform.OS === 'web' ? 'center' : undefined,
        flex: Platform.OS === 'web' ? 0 : 1,
        elevation: 1,
        shadowRadius: 10,
        shadowOpacity: 0.2,
        backgroundColor: buttonColors.background,
        borderRadius: 30,
        borderWidth: 1,
        borderColor: buttonColors.border,
        shadowColor: buttonColors.shadow,
        flexDirection: 'row',
        overflow: 'hidden',
        justifyContent: 'center',
        alignContent: 'center',
        alignItems: 'stretch',
      }}
    >
      {({ hovered }) => (
        <View style={{ flex: 1, width: '100%' }}>
          {code && (
            <Animated.View
              style={{
                ...StyleSheet.absoluteFillObject,
                opacity: fadeAnim,
                borderRadius: 10,
              }}
            >
              <LinearGradient
                colors={['#E4D337', '#FF5733']}
                style={{ flex: 1, borderRadius: 10 }}
              />
            </Animated.View>
          )}

          <View
            style={{
              paddingHorizontal: '10%',
              flex: 1,
              flexDirection: 'row',
              backgroundColor: hovered ? buttonColors.hover : 'rgba(7,193,96,0)',
              borderRadius: 10,
            }}
          >
            <View style={[styles.centerEverything, { flex: 1, maxWidth: 40 }]}>
              <WeChatIcon width={22} height={22} />
            </View>
            <View
              style={{
                flex: 4,
                justifyContent: 'center',
                alignItems: 'center',
                minWidth: Platform.OS === 'web' ? 0 : 220,
              }}
            >
              <Text
                adjustsFontSizeToFit
                allowFontScaling={false}
                style={{ fontSize: responsiveFontSize(15), color: buttonColors.text, fontWeight: '600' }}
              >
                {t('Sign in with WeChat')}
              </Text>
            </View>
          </View>
        </View>
      )}
    </Pressable>
  );
};

export default WeChatButton;
